import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsRejectionModalOpen, selectRejectionModalContext } from '../../store/selectors';
import { closeRejectionModal } from '../../store/slice';
import { Modal, Box, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getRejectionModal } from '../../registry/actionTypeRegistry';
import { RejectionReason, RejectionMetadata, ActionCommand } from '../../types';
import { SentryErrorBoundary } from '../../../../services/SentryService';
import { useUpdateSuggestedActionPreferenceMutation } from '@features/user-preferences/store/api';
import { useTracking } from '../../../../context/TrackingContext';
import { EventType } from '../../../../types/events';
import { useCardManagement } from '../../contexts/CardManagementContext';
import { setPendingCommand } from '../../store/slice';

/**
 * Central rejection modal component that renders different content
 * based on the action type using a registry pattern
 */
export const RejectionModal: React.FC = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsRejectionModalOpen);
  const context = useSelector(selectRejectionModalContext);
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User preferences mutation for disabling features
  const [updateSuggestedActionPreference] = useUpdateSuggestedActionPreferenceMutation();

  // Analytics tracking
  const { trackEvent } = useTracking();

  // Card management functions
  const { resetCard, animateReject, animateAccept } = useCardManagement();

  const handleClose = () => {
    // Reset the card to its original state when modal is canceled
    if (context) {
      resetCard(context.action.action_id);
    }

    setError(null);
    dispatch(closeRejectionModal());
  };

  const handleReject = async (reason: RejectionReason, metadata?: RejectionMetadata) => {
    if (!context) return;

    try {
      setError(null);

      // If user doesn't care for the feature, disable it in preferences
      if (reason === RejectionReason.DONT_CARE_FOR_FEATURE) {
        setIsUpdatingPreferences(true);

        try {
          await updateSuggestedActionPreference({
            actionId: context.actionType,
            updateSuggestedActionPreferenceRequest: {
              enabled: false,
            },
          }).unwrap();

          console.log(`✅ Disabled ${context.actionType} suggestions in user preferences`);
        } catch (preferencesError) {
          console.error('❌ Error updating user preferences:', preferencesError);
          setError('Failed to update your preferences. The suggestion will still be rejected.');
          // Continue with rejection even if preferences update fails
        }
      }

      // Centralized rejection tracking based on step and reason
      trackEvent(EventType.ACTION_REJECTED_REASON_PROVIDED, {
        action_id: context.action.action_id,
        action_type: context.actionType,
        rejection_reason: reason,
        feedback: metadata?.feedback || null,
        encounter_id: context.action.encounter_id || null,
        patient_id: context.action.patient?.patient_id || null,
      });

      // Trigger the card animation and API call
      console.log('Rejection initiated:', { reason, metadata, context });

      try {
        // Prepare rejection data
        const rejectionData = {
          rejection_reason: reason,
          rejection_payload: metadata || {},
        };

        animateReject(context.action.action_id, rejectionData);
        dispatch(closeRejectionModal());
      } catch (rejectionError) {
        console.error('❌ Error rejecting action:', rejectionError);
        setError('Failed to reject the suggestion. Please try again.');
      }
    } catch (error) {
      console.error('❌ Unexpected error during rejection:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdatingPreferences(false);
    }
  };

  const handleAccept = async (actionId: string, updatedPayload: any) => {
    if (!context) return;

    try {
      setError(null);

      // Trigger the card animation which will handle the API call
      animateAccept(actionId, updatedPayload);

      // Close modal immediately - the animation will handle the API call
      dispatch(closeRejectionModal());
    } catch (error) {
      console.error('❌ Error accepting action with edits:', error);
      setError('Failed to accept the suggestion. Please try again.');
    }
  };

  const handleCommand = (command: ActionCommand) => {
    if (!context) return;

    // Dispatch the command to be processed by Actions component
    // This ensures validation and other checks are properly performed
    dispatch(
      setPendingCommand({
        actionId: context.action.action_id,
        command,
      }),
    );

    // Close the modal
    dispatch(closeRejectionModal());
  };

  if (!isOpen || !context) {
    return null;
  }

  // Get the appropriate content component for this action type
  const ContentComponent = getRejectionModal(context.actionType);

  // If no rejection modal is defined for this action type, don't render anything
  if (!ContentComponent) {
    console.warn(`No rejection modal defined for action type: ${context.actionType}`);
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="rejection-modal-title"
      aria-describedby="rejection-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 550,
          maxWidth: '90vw',
          maxHeight: '90dvh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography id="rejection-modal-title" variant="h6" component="h2">
            Why are you rejecting this suggestion?
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Modal Content */}
        <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'error.light',
                borderRadius: 1,
                color: 'error.contrastText',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          <SentryErrorBoundary
            fallback={({ error, resetError }) => (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="subtitle1" color="error.main" sx={{ mb: 2 }}>
                  Unable to load rejection options
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Something went wrong. Please try again.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button onClick={resetError} variant="outlined" color="primary">
                    Try Again
                  </Button>
                  <Button onClick={handleClose} variant="outlined" color="inherit">
                    Close
                  </Button>
                </Box>
              </Box>
            )}
            beforeCapture={scope => {
              scope.setTag('component', 'RejectionModalContent');
              scope.setContext('modal', {
                actionType: context.actionType,
                actionId: context.action.action_id,
              });
            }}
          >
            <ContentComponent
              context={context}
              onReject={handleReject}
              onCancel={handleClose}
              onAccept={handleAccept}
              onCommand={handleCommand}
              isLoading={isUpdatingPreferences}
            />
          </SentryErrorBoundary>
        </Box>
      </Box>
    </Modal>
  );
};
